import { ReactElement, useEffect, useState } from "react";
import clsx from "clsx";

import CustomModal from "src/components/custom-modal/custom-modal";
import DeleteModal from "src/components/delete-modal/delete-modal";

interface RejectModalProps {
  isVisible: boolean;
  busy: boolean;
  onClose: () => void;
  onConfirm: (feedback: string) => void;
}

/**
 * Rejection returns the record to the client as Pending Resubmission.
 *
 * The API requires nonblank feedback, so an empty box is blocked here — that
 * enforces a documented rule of the endpoint rather than second-guessing it,
 * and saves a round trip that would fail anyway.
 */
const RejectModal: React.FC<RejectModalProps> = ({
  isVisible,
  busy,
  onClose,
  onConfirm,
}): ReactElement => {
  const [feedback, setFeedback] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setFeedback("");
      setTouched(false);
    }
  }, [isVisible]);

  const blank = feedback.trim().length === 0;

  return (
    <CustomModal
      isVisible={isVisible}
      centered
      width={450}
      showClose={false}
      onClose={onClose}
      className="delete-modal"
    >
      <DeleteModal
        icon="/images/reject-icon.svg"
        text="Return to client"
        subText="The request goes back to the client as Pending Resubmission with your feedback. Nothing is generated or sent."
        confirmText={busy ? "Sending…" : "Return to client"}
        confirmDisabled={busy || blank}
        onCancel={onClose}
        onConfirm={() => {
          setTouched(true);
          if (!blank) onConfirm(feedback.trim());
        }}
      >
        <div className="modal-field">
          <label htmlFor="wm-feedback">Feedback for the client</label>
          <textarea
            id="wm-feedback"
            value={feedback}
            placeholder="Explain what the client needs to correct"
            className={clsx(touched && blank && "invalid")}
            onChange={(event) => setFeedback(event.target.value)}
            onBlur={() => setTouched(true)}
          />
          <p className={blank ? "error" : "hint"}>
            {blank
              ? "Feedback is required to return a request."
              : "The client sees this feedback."}
          </p>
        </div>
      </DeleteModal>
    </CustomModal>
  );
};

export default RejectModal;
